import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { StagePill, SectionHeader, RatingBadge } from "@/components/common/Widgets";
import { fmtDate, fmtMoney } from "@/lib/format";
import { ChevronLeft, Download, Sparkles, FileText, FileCheck2, RefreshCw, History } from "lucide-react";
import KYCSection from "@/components/detail/KYCSection";
import FieldVerificationSection from "@/components/detail/FieldVerificationSection";
import CMASection from "@/components/detail/CMASection";
import CRASection from "@/components/detail/CRASection";
import AssessmentsSection from "@/components/detail/AssessmentsSection";
import SecuritySection from "@/components/detail/SecuritySection";
import DocumentsSection from "@/components/detail/DocumentsSection";
import WorkflowSection from "@/components/detail/WorkflowSection";
import SFormatSection from "@/components/detail/SFormatSection";
import PolicySection from "@/components/detail/PolicySection";
import AASection from "@/components/detail/AASection";
import MakerCheckerActions from "@/components/detail/MakerCheckerActions";
import CoApplicantsSection from "@/components/detail/CoApplicantsSection";
import StockStatementSection from "@/components/detail/StockStatementSection";
import DisbursementSection from "@/components/detail/DisbursementSection";
import GoldCollateralSection from "@/components/detail/GoldCollateralSection";
import VehicleCollateralSection from "@/components/detail/VehicleCollateralSection";
import PropertySection from "@/components/detail/PropertySection";
import EligibilityPreview from "@/components/common/EligibilityPreview";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function ApplicationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [recomputing, setRecomputing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const canRecompute = ["HO", "RO", "BM", "CO", "SUPER_ADMIN"].includes(user?.role);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(`/applications/${id}`);
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load application");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const recomputeEligibility = async () => {
    setRecomputing(true);
    try {
      const r = await api.post(`/applications/${id}/recompute-eligibility`);
      const verdict = r.data?.snapshot?.verdict;
      toast.success(`Eligibility recomputed: ${verdict}`);
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Recompute failed");
    } finally {
      setRecomputing(false);
    }
  };

  if (loading) return <div className="p-6 text-muted-foreground">Loading…</div>;
  if (!data?.application) return <div className="p-6">Application not found.</div>;
  const a = data.application;
  const cust = data.customer || {};
  const cra = data.cra || {};
  const sb = (cra?.result || {}).sb_rating;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ChevronLeft className="h-4 w-4 mr-1" />Back</Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl md:text-2xl heading font-semibold">{cust?.name || a.customer_name}</h1>
              <Badge variant="outline" className="mono text-xs">{a.ref_no}</Badge>
              <StagePill stage={a.stage} />
              {sb && <RatingBadge rating={sb} />}
              {a.origin && <Badge variant="outline" className="text-[10px] border-accent/40 text-accent">Origin: {a.origin}</Badge>}
              {a.assigned_fo_name && <Badge variant="secondary" className="text-[10px]">FO: {a.assigned_fo_name}</Badge>}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5 mono">{a.loan_type} · CIF {a.customer_cif} · Branch {a.branch || "-"} · Updated {fmtDate(a.updated_at)}</div>
          </div>
        </div>
      </div>

      {/* Quick facility summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryStat label="CC Proposed" value={`₹ ${fmtMoney(a.cc_proposed)} L`} />
        <SummaryStat label="TL Proposed" value={`₹ ${fmtMoney(a.tl_proposed)} L`} />
        <SummaryStat label="Tenor" value={a.tenor_months ? `${a.tenor_months} mo` : "—"} />
        <SummaryStat label="Interest" value={a.interest_rate ? `${a.interest_rate}% p.a.` : "—"} />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="flex flex-wrap h-auto p-1 bg-muted/60">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="kyc" data-testid="tab-kyc">{t("tabs.kyc", "KYC")}</TabsTrigger>
          <TabsTrigger value="coapplicants" data-testid="tab-coapplicants">{t("tabs.coApplicants", "Co-Applicants")}</TabsTrigger>
          <TabsTrigger value="fv" data-testid="tab-fv">{t("tabs.fv", "Field Verification")}</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs">{t("tabs.documents", "Documents")}</TabsTrigger>
          <TabsTrigger value="cma" data-testid="tab-cma">{t("tabs.cma", "CMA")}</TabsTrigger>
          <TabsTrigger value="cra" data-testid="tab-cra">{t("tabs.cra", "CRA")}</TabsTrigger>
          <TabsTrigger value="assessments" data-testid="tab-assessments">{t("tabs.assessment", "WC / TL / CGTMSE")}</TabsTrigger>
          {["CC","SME_CC","WC_NAYAK","OD"].includes((a.loan_type || "").toUpperCase()) && (
            <TabsTrigger value="stock" data-testid="tab-stock">{t("tabs.stock", "Stock & DP")}</TabsTrigger>
          )}
          {["GOLD_LOAN"].includes((a.loan_type || "").toUpperCase()) && (
            <TabsTrigger value="gold" data-testid="tab-gold">Gold</TabsTrigger>
          )}
          {["TW_LOAN","FW_LOAN"].includes((a.loan_type || "").toUpperCase()) && (
            <TabsTrigger value="vehicle" data-testid="tab-vehicle">Vehicle</TabsTrigger>
          )}
          {["HOUSING_LOAN","MORTGAGE"].includes((a.loan_type || "").toUpperCase()) && (
            <TabsTrigger value="property" data-testid="tab-property">Property</TabsTrigger>
          )}
          <TabsTrigger value="security" data-testid="tab-security">{t("tabs.security", "Security")}</TabsTrigger>
          <TabsTrigger value="aa" data-testid="tab-aa">{t("tabs.aa", "Account Aggregator")}</TabsTrigger>
          <TabsTrigger value="policy" data-testid="tab-policy">{t("tabs.policy", "Policy & Deviations")}</TabsTrigger>
          <TabsTrigger value="workflow" data-testid="tab-workflow">{t("tabs.workflow", "Workflow")}</TabsTrigger>
          <TabsTrigger value="mc" data-testid="tab-mc">Maker-Checker</TabsTrigger>
          <TabsTrigger value="sformat" data-testid="tab-sformat">{t("tabs.sFormat", "S-Format")}</TabsTrigger>
          <TabsTrigger value="disbursement" data-testid="tab-disbursement">Disbursement</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-3">
          <Card className="p-4">
            <SectionHeader title="Borrower Snapshot" subtitle="" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Row k="Constitution" v={cust.constitution} />
              <Row k="PAN" v={cust.pan} />
              <Row k="GSTIN" v={cust.gstin} />
              <Row k="Mobile" v={cust.mobile} />
              <Row k="Activity" v={cust.activity} />
              <Row k="Branch" v={cust.branch} />
              <Row k="CIBIL" v={cust.cibil_score} />
              <Row k="Promoter" v={cust.promoter_name} />
              <Row k="Promoter Experience" v={cust.promoter_experience_years && `${cust.promoter_experience_years} years`} />
              <Row k="Address" v={cust.address} colSpan />
            </div>
          </Card>
          {a.eligibility_snapshot && (
            <Card className="p-4" data-testid="overview-eligibility-snapshot">
              <SectionHeader
                title="Eligibility Snapshot"
                subtitle="FOIR-based assessment computed at application creation / lead conversion. Auto-recomputes when income, expense, EMI, tenor, ROI or loan amount changes."
                action={(
                  <div className="flex gap-2">
                    {(a.eligibility_snapshot_history || []).length > 0 && (
                      <Button size="sm" variant="outline" onClick={() => setHistoryOpen(true)} data-testid="eligibility-history-button">
                        <History className="h-3.5 w-3.5 mr-1" />History ({(a.eligibility_snapshot_history || []).length})
                      </Button>
                    )}
                    {canRecompute && a.loan_type && ["PERSONAL_LOAN", "HOUSING_LOAN"].includes(a.loan_type) && (
                      <Button size="sm" onClick={recomputeEligibility} disabled={recomputing} data-testid="eligibility-recompute-button">
                        <RefreshCw className={`h-3.5 w-3.5 mr-1 ${recomputing ? "animate-spin" : ""}`} />
                        {recomputing ? "Recomputing…" : "Recompute"}
                      </Button>
                    )}
                  </div>
                )}
              />
              <EligibilityPreview snapshot={a.eligibility_snapshot} dataTestid="overview-eligibility-preview" />
            </Card>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-4">
              <SectionHeader title="Latest Audit Activity" subtitle="Last 6 events" />
              <ul className="space-y-3">
                {(data.audit_log || []).slice(0, 6).map((e) => (
                  <li key={e.id} className="text-sm flex items-start gap-2.5">
                    <div className="mt-1 h-2 w-2 rounded-full bg-accent" />
                    <div className="flex-1">
                      <div className="font-medium">{e.actor_name} <span className="text-xs text-muted-foreground">({e.actor_role})</span></div>
                      <div className="text-xs text-accent mono">{e.action}</div>
                      <div className="text-[11px] text-muted-foreground mono">{fmtDate(e.timestamp)}</div>
                    </div>
                  </li>
                ))}
                {(data.audit_log || []).length === 0 && <li className="text-sm text-muted-foreground">No activity yet.</li>}
              </ul>
            </Card>
            <Card className="p-4">
              <SectionHeader title="Outputs Ready" subtitle="Generated artifacts" />
              <div className="space-y-2">
                <ArtifactRow label="CMA (Excel)" enabled={!!data.cma} url={`/api/exports/cma/${a.id}.xlsx`} icon={FileCheck2} testid="download-cma" />
                <ArtifactRow label="S-Format Appraisal (PDF)" enabled={!!data.s_format} url={`/api/exports/s-format/${a.id}.pdf`} icon={FileText} testid="download-sformat" />
                <ArtifactRow label="Sanction Letter (PDF)" enabled={a.stage === 'SANCTIONED'} url={`/api/exports/sanction-letter/${a.id}.pdf`} icon={Sparkles} testid="download-sanction" />
              </div>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="kyc"><KYCSection app={a} customer={cust} reload={fetchData} /></TabsContent>
        <TabsContent value="coapplicants"><CoApplicantsSection app={a} coApplicants={a.co_applicants || []} reload={fetchData} /></TabsContent>
        <TabsContent value="fv"><FieldVerificationSection app={a} fvList={data.field_verifications || []} reload={fetchData} /></TabsContent>
        <TabsContent value="docs"><DocumentsSection app={a} docs={data.documents || []} reload={fetchData} /></TabsContent>
        <TabsContent value="cma"><CMASection app={a} cma={data.cma} reload={fetchData} /></TabsContent>
        <TabsContent value="cra"><CRASection app={a} cma={data.cma} cra={data.cra} customer={cust} reload={fetchData} /></TabsContent>
        <TabsContent value="assessments"><AssessmentsSection app={a} assessments={data.assessments || []} reload={fetchData} /></TabsContent>
        {["CC","SME_CC","WC_NAYAK","OD"].includes((a.loan_type || "").toUpperCase()) && (
          <TabsContent value="stock"><StockStatementSection app={a} reload={fetchData} /></TabsContent>
        )}
        <TabsContent value="security"><SecuritySection app={a} security={data.security || []} reload={fetchData} /></TabsContent>
        <TabsContent value="aa"><AASection app={a} customer={cust} consents={data.aa_consents || []} fiData={data.aa_fi_data || []} reload={fetchData} /></TabsContent>
        <TabsContent value="policy"><PolicySection app={a} policy={data.policy} reload={fetchData} /></TabsContent>
        <TabsContent value="workflow"><WorkflowSection app={a} approvals={data.approvals || []} audit={data.audit_log || []} reload={fetchData} /></TabsContent>
        <TabsContent value="mc"><MakerCheckerActions app={a} pending={data.pending_approvals || []} reload={fetchData} /></TabsContent>
        <TabsContent value="sformat"><SFormatSection app={a} sformat={data.s_format} reload={fetchData} /></TabsContent>
        <TabsContent value="disbursement"><DisbursementSection app={a} reload={fetchData} /></TabsContent>
        <TabsContent value="gold"><GoldCollateralSection app={a} reload={fetchData} /></TabsContent>
        <TabsContent value="vehicle"><VehicleCollateralSection app={a} reload={fetchData} /></TabsContent>
        <TabsContent value="property"><PropertySection app={a} reload={fetchData} /></TabsContent>
      </Tabs>

      {/* Eligibility snapshot history dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="eligibility-history-dialog">
          <DialogHeader>
            <DialogTitle>Eligibility Snapshot History</DialogTitle>
            <DialogDescription>Previous FOIR-based assessments (newest first) — useful to audit how the verdict has changed as the application has been updated.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {[...(a.eligibility_snapshot_history || [])].reverse().map((snap, i) => (
              <div key={i} className="space-y-1">
                <div className="text-xs text-muted-foreground">Snapshot #{(a.eligibility_snapshot_history || []).length - i}</div>
                <EligibilityPreview snapshot={snap} compact dataTestid={`eligibility-history-item-${i}`} />
              </div>
            ))}
            {(a.eligibility_snapshot_history || []).length === 0 && (
              <div className="text-sm text-muted-foreground py-8 text-center">No prior snapshots.</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryStat({ label, value }) {
  return (
    <Card className="p-3 shadow-card">
      <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">{label}</div>
      <div className="mt-1 text-lg md:text-xl mono font-semibold tabular">{value}</div>
    </Card>
  );
}

function Row({ k, v, colSpan }) {
  return (
    <div className={colSpan ? "md:col-span-2" : ""}>
      <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium">{k}</div>
      <div className="text-sm mt-0.5">{v || "—"}</div>
    </div>
  );
}

function ArtifactRow({ label, enabled, url, icon: Icon, testid }) {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
  const handleDownload = () => {
    if (!enabled) return;
    const fullUrl = `${BACKEND_URL}${url}`;
    const token = localStorage.getItem("acas_token");
    fetch(fullUrl, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error("download failed"); return r.blob(); })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = url.split("/").pop();
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(objectUrl);
      })
      .catch(() => toast.error("Download failed"));
  };
  return (
    <div className="flex items-center justify-between border rounded-md px-3 py-2">
      <div className="flex items-center gap-2 text-sm">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <span>{label}</span>
      </div>
      <Button variant="outline" size="sm" disabled={!enabled} onClick={handleDownload} data-testid={testid}>
        <Download className="h-3.5 w-3.5 mr-1" />Download
      </Button>
    </div>
  );
}
